/**
 * 처방 검수 목록 화면.
 * admin-ruda-web `prescriptionReview.js` 포팅.
 * 구성: PageHeader → FilterCard(+OCR 통계) → DataCard (다른 목록과 동일).
 */
import {
  useCreateDrugPrescriptions,
  useDeletePrescriptionDrugsByImageCode,
  useFinalizePrescriptionsBySettlementMonth,
  usePrescriptionList,
} from '@/entities/prescription/hooks/usePrescriptions';
import { EmptyState } from '@/shared/components/EmptyState';
import { DataCard } from '@/shared/components/DataCard';
import { FileUploadButton } from '@/shared/components/FileUploadButton';
import { MonthPicker } from '@/shared/components/DatePicker';
import { FilterCard } from '@/shared/components/FilterCard';
import { LoadingSpinner } from '@/shared/components/LoadingSpinner';
import { MenuWriteGate } from '@/shared/components/MenuWriteGate';
import { PageHeader } from '@/shared/components/PageHeader';
import { SimplePagination } from '@/shared/components/SimplePagination';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/shared/components/ui/alert-dialog';
import { Badge } from '@/shared/components/ui/badge';
import { Button } from '@/shared/components/ui/button';
import { Checkbox } from '@/shared/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { Field } from '@/shared/components/ui/form-field';
import { Input } from '@/shared/components/ui/input';
import { Label } from '@/shared/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/components/ui/table';
import { FilterRow, ToolbarRow } from '@/shared/components/ui/row';
import { prescriptionReviewDetailPath } from '@/shared/constants/routes';
import { useListSearchParams } from '@/shared/hooks/useListSearchParams';
import { toUrlSearch } from '@/shared/lib/listSearchParams';
import { cn } from '@/shared/lib/utils';
import { importFromXlsx } from '@/shared/utils/xlsxExport';
import { handleApiError, useToast } from '@/shared/utils/toast';
import { useNavigate } from '@tanstack/react-router';
import {
  AlertCircle,
  CheckCircle2,
  FileText,
  Loader2,
  MinusCircle,
  Plus,
  Download,
  Upload,
} from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { AddPrescriptionDialog } from '@/pages/prescriptions/AddPrescriptionDialog';
import { OcrMatchStatsCard } from '@/pages/prescriptions/OcrMatchStatsCard';
import {
  downloadPrescriptionDrugUploadTemplate,
  mapDrugUploadRow,
  PRESCRIPTION_DRUG_UPLOAD_HEADER_MAP,
} from '@/pages/prescriptions/prescriptionReviewExportUtils';
import {
  AUTO_OCR_ACTIVE_STATUSES,
  getAutoOcrStatusMeta,
} from '@/pages/prescriptions/prescriptionOcrUtils';
import {
  getAdminCheckStatusBadgeVariant,
  getAdminCheckStatusLabel,
  getExclusionReasonLabel,
  getExclusionReasonShortLabel,
  formatYearMonth,
  getDefaultSettlementYm,
} from '@/pages/prescriptions/prescriptionUtils';
import { formatDate } from '@/shared/lib/dateFormat';
import type {
  CreateDrugPrescriptionsRequest,
  PrescriptionAdminCheckStatusFilter,
} from '@/entities/prescription/api/prescription.api';

const DrugExistIcon: React.FC<{
  mrDrugCount: number;
  allDrugsExist: boolean;
}> = ({ mrDrugCount, allDrugsExist }) => {
  if (mrDrugCount === 0) {
    return <MinusCircle className="h-4 w-4 text-muted-foreground" />;
  }
  return allDrugsExist ? (
    <CheckCircle2 className="h-4 w-4 text-green-600" />
  ) : (
    <AlertCircle className="h-4 w-4 text-red-500" />
  );
};

const AutoMatchStatusCell: React.FC<{ ocrStatus?: string }> = ({
  ocrStatus,
}) => {
  const meta = getAutoOcrStatusMeta(ocrStatus);
  const isActive =
    !!ocrStatus && AUTO_OCR_ACTIVE_STATUSES.has(ocrStatus as never);

  return (
    <Badge variant={meta.variant} className="inline-flex items-center gap-1">
      {isActive ? <Loader2 className="h-3 w-3 animate-spin" /> : null}
      {meta.label}
    </Badge>
  );
};

const PRESCRIPTION_REVIEW_SEARCH_DEFAULTS = {
  page: 1,
  pageSize: 25,
  settlementYearMonth: getDefaultSettlementYm(),
  clinicName: '',
  userEmail: '',
  companyName: '',
  filterModified: false,
  filterVerified: false,
  filterUnverified: false,
  filterExcluded: false,
};

export const PrescriptionReviewPage: React.FC = () => {
  const toast = useToast();
  const navigate = useNavigate();

  const { values, setValues } = useListSearchParams(
    PRESCRIPTION_REVIEW_SEARCH_DEFAULTS,
  );

  const [settlementYmInput, setSettlementYmInput] = useState(
    values.settlementYearMonth,
  );
  const [clinicInput, setClinicInput] = useState(values.clinicName);
  const [emailInput, setEmailInput] = useState(values.userEmail);
  const [companyInput, setCompanyInput] = useState(values.companyName);

  useEffect(() => {
    setSettlementYmInput(values.settlementYearMonth);
    setClinicInput(values.clinicName);
    setEmailInput(values.userEmail);
    setCompanyInput(values.companyName);
  }, [
    values.settlementYearMonth,
    values.clinicName,
    values.userEmail,
    values.companyName,
  ]);

  const search = useMemo(() => {
    const statuses: PrescriptionAdminCheckStatusFilter[] = [];
    if (values.filterModified) statuses.push('MODIFIED');
    if (values.filterVerified) statuses.push('VERIFIED');
    if (values.filterUnverified) statuses.push('UNVERIFIED');
    // 서버: 검수 상태 값은 미제외 이미지만, EXCLUDED 는 제외 이미지만 거른다
    if (values.filterExcluded) statuses.push('EXCLUDED');

    return {
      settlementYearMonth: values.settlementYearMonth,
      clinicName: values.clinicName || undefined,
      userEmail: values.userEmail || undefined,
      companyName: values.companyName || undefined,
      adminCheckStatus: statuses.length > 0 ? statuses : undefined,
    };
  }, [values]);

  const pageable = useMemo(
    () => ({ page: Math.max(0, values.page - 1), size: values.pageSize }),
    [values.page, values.pageSize],
  );

  const [finalizeDialogOpen, setFinalizeDialogOpen] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [uploadRows, setUploadRows] = useState<
    CreateDrugPrescriptionsRequest[]
  >([]);
  const [imageCodesForDeletion, setImageCodesForDeletion] = useState<string[]>(
    [],
  );

  const finalizeByMonth = useFinalizePrescriptionsBySettlementMonth();
  const createDrugPrescriptions = useCreateDrugPrescriptions();
  const deleteByImageCodes = useDeletePrescriptionDrugsByImageCode();

  const { data, isLoading, isError, error } = usePrescriptionList({
    search,
    pageable,
  });

  useEffect(() => {
    if (isError && error) {
      handleApiError(error, toast);
    }
  }, [isError, error, toast]);

  const prescriptions = data?.content ?? [];
  const totalCount = data?.totalCount ?? 0;
  const ocrMatchStats = data?.ocrMatchStats ?? null;

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setValues({
      settlementYearMonth: settlementYmInput.trim() || getDefaultSettlementYm(),
      clinicName: clinicInput.trim(),
      userEmail: emailInput.trim(),
      companyName: companyInput.trim(),
      page: 1,
    });
  };

  /** 정산년월 MonthPicker 변경 시 즉시 목록 재조회 */
  const handleSettlementYmChange = (next: string) => {
    const ym = next.trim() || getDefaultSettlementYm();
    setSettlementYmInput(ym);
    setValues({
      settlementYearMonth: ym,
      page: 1,
    });
  };

  const handleFinalizeAll = async () => {
    const ym = values.settlementYearMonth.trim() || getDefaultSettlementYm();
    if (!ym) {
      toast.error('정산 년월을 먼저 선택해 주세요.');
      return;
    }
    try {
      await finalizeByMonth.mutateAsync(ym);
      // 미노출·확정 제외 건은 서버가 조용히 건너뛰므로 목록을 다시 확인하도록 안내
      toast.success(
        '선택한 정산 년월의 처방을 확정했습니다. (미노출·확정 제외 건은 빠질 수 있습니다)',
      );
      setFinalizeDialogOpen(false);
    } catch (e) {
      handleApiError(e, toast);
    }
  };

  const handleUploadFile = async (file: File) => {
    try {
      const parsed = await importFromXlsx(
        file,
        PRESCRIPTION_DRUG_UPLOAD_HEADER_MAP,
      );
      const rows = parsed.map(mapDrugUploadRow);
      setUploadRows(rows);
      const codes: string[] = [];
      for (const row of rows) {
        const code = row.prescriptionImageCode?.trim();
        if (code && !codes.includes(code)) codes.push(code);
      }
      setImageCodesForDeletion(codes);
    } catch (e) {
      handleApiError(e, toast);
      setUploadRows([]);
      setImageCodesForDeletion([]);
    }
  };

  const resetUploadState = () => {
    setUploadRows([]);
    setImageCodesForDeletion([]);
  };

  const handleBatchUpload = async () => {
    if (uploadRows.length === 0) {
      toast.error('업로드할 데이터가 없습니다.');
      return;
    }
    try {
      if (imageCodesForDeletion.length > 0) {
        await deleteByImageCodes.mutateAsync(imageCodesForDeletion);
        toast.success('처방 정보 삭제 완료');
      }
      await createDrugPrescriptions.mutateAsync(uploadRows);
      toast.success('처방 정보 업로드 완료');
      setUploadDialogOpen(false);
      resetUploadState();
    } catch (e) {
      handleApiError(e, toast);
    }
  };

  return (
    <div className="space-y-5">
      <PageHeader
        icon={FileText}
        title="처방 검수"
        description="처방 검수 및 확정 처리"
        actions={
          <MenuWriteGate>
            <Button variant="outline" onClick={() => setAddDialogOpen(true)}>
              <Plus className="mr-2 h-4 w-4" />
              처방전 등록
            </Button>
            <Button
              className="ruda-button"
              onClick={() => {
                resetUploadState();
                setUploadDialogOpen(true);
              }}
            >
              <Upload className="mr-2 h-4 w-4" />
              처방 정보 업로드
            </Button>
            <Button
              className="ruda-button"
              disabled={finalizeByMonth.isPending}
              onClick={() => {
                if (!values.settlementYearMonth.trim()) {
                  toast.error('정산 년월을 먼저 선택해 주세요.');
                  return;
                }
                setFinalizeDialogOpen(true);
              }}
            >
              확정 처방 재확정
            </Button>
          </MenuWriteGate>
        }
      />

      {/* 레거시 SearchFilter + rightSlot(OCR) — 의약품 목록과 동일: 년월 단독 행 */}
      <FilterCard>
        <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
          <form className="min-w-0 flex-1 space-y-4" onSubmit={handleSearch}>
            <FilterRow as="div" gap={12}>
              <Field label="정산 년월" htmlFor="settlement-ym">
                <MonthPicker
                  id="settlement-ym"
                  placeholder="년월 선택"
                  value={settlementYmInput}
                  onChange={handleSettlementYmChange}
                  className="w-44"
                />
              </Field>
            </FilterRow>

            <FilterRow as="div" gap={12}>
              <Field label="병의원명" htmlFor="rx-clinic">
                <Input
                  id="rx-clinic"
                  value={clinicInput}
                  onChange={(e) => setClinicInput(e.target.value)}
                  className="w-36"
                />
              </Field>
              <Field label="사용자 이메일" htmlFor="rx-email">
                <Input
                  id="rx-email"
                  value={emailInput}
                  onChange={(e) => setEmailInput(e.target.value)}
                  className="w-44"
                />
              </Field>
              <Field label="업체명" htmlFor="rx-company">
                <Input
                  id="rx-company"
                  value={companyInput}
                  onChange={(e) => setCompanyInput(e.target.value)}
                  className="w-36"
                />
              </Field>
              <Button type="submit" className="ruda-button">
                검색
              </Button>
            </FilterRow>

            <div className="space-y-2">
              <Label>관리자 확인 상태</Label>
              <div className="flex flex-wrap gap-6 text-sm">
                <label
                  htmlFor="filter-modified"
                  className="flex cursor-pointer items-center gap-2"
                >
                  <Checkbox
                    id="filter-modified"
                    checked={values.filterModified}
                    onCheckedChange={(checked) =>
                      setValues({
                        filterModified: checked === true,
                        page: 1,
                      })
                    }
                  />
                  수정
                </label>
                <label
                  htmlFor="filter-verified"
                  className="flex cursor-pointer items-center gap-2"
                >
                  <Checkbox
                    id="filter-verified"
                    checked={values.filterVerified}
                    onCheckedChange={(checked) =>
                      setValues({
                        filterVerified: checked === true,
                        page: 1,
                      })
                    }
                  />
                  확인
                </label>
                <label
                  htmlFor="filter-unverified"
                  className="flex cursor-pointer items-center gap-2"
                >
                  <Checkbox
                    id="filter-unverified"
                    checked={values.filterUnverified}
                    onCheckedChange={(checked) =>
                      setValues({
                        filterUnverified: checked === true,
                        page: 1,
                      })
                    }
                  />
                  미확인
                </label>
                <label
                  htmlFor="filter-excluded"
                  className="flex cursor-pointer items-center gap-2"
                >
                  <Checkbox
                    id="filter-excluded"
                    checked={values.filterExcluded}
                    onCheckedChange={(checked) =>
                      setValues({
                        filterExcluded: checked === true,
                        page: 1,
                      })
                    }
                  />
                  확정 제외
                </label>
              </div>
            </div>
          </form>

          {ocrMatchStats ? (
            <OcrMatchStatsCard
              stats={ocrMatchStats}
              variant="card"
              className="xl:shrink-0"
            />
          ) : null}
        </div>
      </FilterCard>

      <DataCard>
        {isLoading ? (
          <div className="py-16">
            <LoadingSpinner tip="처방 목록을 불러오는 중..." />
          </div>
        ) : prescriptions.length === 0 ? (
          <EmptyState description="검색 조건에 맞는 처방이 없습니다." />
        ) : (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>업체명</TableHead>
                  <TableHead>사용자 이메일</TableHead>
                  <TableHead>병의원명</TableHead>
                  <TableHead>처방 의약품 수</TableHead>
                  <TableHead>의약품 정보 존재 여부</TableHead>
                  <TableHead>등록일</TableHead>
                  <TableHead>처방 년월</TableHead>
                  <TableHead>이미지 고유 코드</TableHead>
                  <TableHead>관리자 확인 상태</TableHead>
                  <TableHead>자동 매칭</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {prescriptions.map((rx) => {
                  const goDetail = () =>
                    navigate({
                      to: prescriptionReviewDetailPath(
                        rx.prescriptionImageCode,
                      ),
                      search: { refYm: rx.prescriptionYearMonth },
                      // 삭제·나가기 시 목록 조회조건 복원
                      state: {
                        fromPrescriptionReviewList: true,
                        prescriptionReviewListSearch: toUrlSearch(
                          values,
                          PRESCRIPTION_REVIEW_SEARCH_DEFAULTS,
                        ),
                      },
                    } as never);
                  const missingDrugInfo =
                    rx.mrDrugCount > 0 && rx.allDrugsExist === false;

                  return (
                    <TableRow
                      key={rx.prescriptionImageCode}
                      className={cn(
                        'cursor-pointer hover:bg-muted/50',
                        missingDrugInfo && 'bg-red-50',
                      )}
                      onClick={goDetail}
                    >
                      <TableCell>
                        {rx.companyName || rx.ceoName || '-'}
                      </TableCell>
                      <TableCell>{rx.userEmail}</TableCell>
                      <TableCell className="font-medium">
                        {rx.clinicName}
                      </TableCell>
                      <TableCell>
                        {rx.mrDrugCount.toLocaleString('ko-KR')}
                      </TableCell>
                      <TableCell>
                        <DrugExistIcon
                          mrDrugCount={rx.mrDrugCount}
                          allDrugsExist={rx.allDrugsExist}
                        />
                      </TableCell>
                      <TableCell>{formatDate(rx.registrationDate)}</TableCell>
                      <TableCell>
                        {formatYearMonth(rx.prescriptionYearMonth)}
                      </TableCell>
                      <TableCell>{rx.prescriptionImageCode}</TableCell>
                      <TableCell>
                        {/*
                          제외는 검수 상태와 별도 컬럼이라 서버는 원래 검수 상태(예: UNVERIFIED)를 그대로 준다.
                          목록에선 필터(EXCLUDED)와 맞도록 제외 태그만 보이고, 원래 상태는 상세에서 확인한다.
                        */}
                        {rx.exclusionReason ? (
                          <Badge
                            className="border-transparent bg-ruda-warning text-white"
                            title={getExclusionReasonLabel(rx.exclusionReason)}
                          >
                            제외 ·{' '}
                            {getExclusionReasonShortLabel(rx.exclusionReason)}
                          </Badge>
                        ) : (
                          <Badge
                            variant={getAdminCheckStatusBadgeVariant(
                              rx.adminCheckStatus,
                            )}
                          >
                            {getAdminCheckStatusLabel(rx.adminCheckStatus)}
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <AutoMatchStatusCell ocrStatus={rx.ocrStatus} />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            <SimplePagination
              page={values.page}
              pageSize={values.pageSize}
              total={totalCount}
              onPageChange={(page) => setValues({ page })}
              onPageSizeChange={(pageSize) => setValues({ pageSize, page: 1 })}
            />
          </>
        )}
      </DataCard>

      <AlertDialog
        open={finalizeDialogOpen}
        onOpenChange={setFinalizeDialogOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>확정 처방 재확정</AlertDialogTitle>
            <AlertDialogDescription>
              선택한 정산 년월(
              {formatYearMonth(values.settlementYearMonth)})의 처방을 모두
              확정하시겠습니까?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>취소</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleFinalizeAll}
              disabled={finalizeByMonth.isPending}
            >
              확인
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog
        open={uploadDialogOpen}
        onOpenChange={(open) => {
          setUploadDialogOpen(open);
          if (!open) resetUploadState();
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>처방 정보 업로드</DialogTitle>
            <DialogDescription>
              XLSX 양식에 맞춘 파일을 업로드합니다. 동일 이미지 코드의 기존 처방
              정보는 삭제 후 등록됩니다.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <ToolbarRow gap={8}>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={downloadPrescriptionDrugUploadTemplate}
              >
                <Download className="mr-2 h-4 w-4" />
                양식 다운로드
              </Button>
              <FileUploadButton
                accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                onFilesSelected={(files) => {
                  const file = files?.[0];
                  if (file) void handleUploadFile(file);
                }}
              >
                파일 선택
              </FileUploadButton>
            </ToolbarRow>
            {imageCodesForDeletion.length > 0 ? (
              <div className="space-y-2 rounded-lg border p-3 text-sm">
                <p className="font-medium">삭제할 처방 정보</p>
                <ul className="max-h-32 list-disc overflow-auto pl-5 text-muted-foreground">
                  {imageCodesForDeletion.map((code) => (
                    <li key={code}>{code}</li>
                  ))}
                </ul>
                <p className="text-muted-foreground">
                  처방 정보를 업로드하면 기존 처방 정보가 삭제됩니다.
                </p>
              </div>
            ) : null}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setUploadDialogOpen(false);
                resetUploadState();
              }}
            >
              취소
            </Button>
            <Button
              onClick={handleBatchUpload}
              disabled={
                uploadRows.length === 0 ||
                createDrugPrescriptions.isPending ||
                deleteByImageCodes.isPending
              }
            >
              확인
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AddPrescriptionDialog
        open={addDialogOpen}
        onOpenChange={setAddDialogOpen}
      />
    </div>
  );
};
